import { FlaskConical } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useLisResultsForProduct } from '@/hooks/useLisOrders'
import type { LisObservation, LisResult } from '@/api/lisApi'

/**
 * What the laboratory reported for one product, shown as sent.
 *
 * Deliberately raw. Values are displayed exactly as the message carried them —
 * a coded organism arrives whole, a `<^0.5` MIC keeps its operator — because
 * this panel's job is to let someone check the screen against the message, not
 * to interpret it.
 *
 * Whether a value feeds the dose calculation is not shown here: that is decided
 * by the LIS Mapping configuration (a mapping marked as a Dose input), and the
 * dose reads the form field the value was written to, not the observation.
 */
export function LisResultsPanel({ productId }: { productId: string | null }) {
  const results = useLisResultsForProduct(productId)

  if (results.isPending) {
    return <p className='text-sm text-muted-foreground'>Loading laboratory results…</p>
  }
  if (results.isError) {
    return <p className='text-sm text-destructive'>Could not load laboratory results.</p>
  }

  const rows = results.data ?? []
  if (rows.length === 0) {
    // A stated reason, not an empty table: no results is the normal state until
    // a test has been ordered and the laboratory has reported.
    return (
      <p className='rounded-xl border bg-white p-4 text-sm text-muted-foreground shadow-sm'>
        No laboratory results for this product yet. They arrive after a test is ordered from the
        case page, usually hours later.
      </p>
    )
  }

  return (
    <div className='space-y-4'>
      {rows.map((result) => (
        <ResultCard key={result.id} result={result} />
      ))}
    </div>
  )
}

function ResultCard({ result }: { result: LisResult }) {
  return (
    <div className='rounded-xl border bg-white shadow-sm'>
      <div className='flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-3'>
        <FlaskConical className='h-4 w-4 shrink-0 text-muted-foreground' />
        <span className='font-medium'>
          {result.testCode}
          {result.testName ? ` — ${result.testName}` : ''}
        </span>
        <StatusChip status={result.resultStatus} />
        {result.notDirectlyOrdered ? (
          // A panel or reflex test: nobody asked for this code, Beaker added it.
          <span className='rounded-full border border-border/60 bg-muted/50 px-2 py-0.5 text-[11px] text-muted-foreground'>
            Not directly ordered
          </span>
        ) : null}
        {result.parentValue ? (
          <span className='rounded-full border border-border/60 bg-muted/50 px-2 py-0.5 text-[11px] text-muted-foreground'>
            Susceptibilities for {result.parentValue}
          </span>
        ) : null}
      </div>

      <dl className='flex flex-wrap gap-x-6 gap-y-1 border-b px-4 py-2 text-xs text-muted-foreground'>
        <Meta label='Order ID' value={result.orderId ?? 'unsolicited'} mono />
        <Meta label='Laboratory no.' value={result.fillerOrderId} mono />
        {result.stage ? <Meta label='Stage' value={result.stage} /> : null}
        <Meta
          label='Reported'
          value={result.sentAt ? new Date(result.sentAt).toLocaleString() : '—'}
        />
      </dl>

      <div className='overflow-x-persistent'>
        <table className='w-full text-sm'>
          <thead>
            <tr className='border-b text-left text-[11px] uppercase tracking-wider text-muted-foreground'>
              <th className='px-4 py-2 font-medium'>Result</th>
              <th className='px-4 py-2 font-medium'>Value</th>
              <th className='px-4 py-2 font-medium'>Units</th>
              <th className='px-4 py-2 font-medium'>Reference</th>
              <th className='px-4 py-2 font-medium'>Flag</th>
            </tr>
          </thead>
          <tbody>
            {result.observations.map((obs) => (
              <ObservationRow key={obs.id} obs={obs} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ObservationRow({ obs }: { obs: LisObservation }) {
  return (
    <tr className='border-b last:border-0'>
      <td className='px-4 py-2'>
        <span className='font-mono text-xs'>{obs.code}</span>
        {obs.codeText ? (
          <span className='ml-2 text-xs text-muted-foreground'>{obs.codeText}</span>
        ) : null}
        {obs.subId ? (
          <span className='ml-2 text-[11px] text-muted-foreground'>isolate {obs.subId}</span>
        ) : null}
      </td>
      {/* Exactly as sent — never reformatted. A reviewer has to be able to
          compare this against the HL7 message character for character. */}
      <td className='px-4 py-2 font-mono text-xs'>{obs.value ?? '—'}</td>
      <td className='px-4 py-2 text-xs text-muted-foreground'>{obs.units ?? '—'}</td>
      <td className='px-4 py-2 text-xs text-muted-foreground'>{obs.referenceRange ?? '—'}</td>
      <td className='px-4 py-2'>
        {obs.abnormalFlag ? (
          <span
            className={cn(
              'rounded px-1.5 py-0.5 font-mono text-[11px]',
              obs.abnormalFlag === 'R'
                ? 'bg-destructive/10 text-destructive'
                : obs.abnormalFlag === 'S'
                  ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                  : 'bg-muted text-muted-foreground',
            )}
          >
            {obs.abnormalFlag}
          </span>
        ) : (
          <span className='text-xs text-muted-foreground'>—</span>
        )}
      </td>
    </tr>
  )
}

function StatusChip({ status }: { status: string | null }) {
  if (!status) return null
  const label =
    status === 'F' ? 'Final' : status === 'P' ? 'Preliminary' : status === 'C' ? 'Corrected' : status
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-[11px]',
        status === 'C'
          ? 'bg-amber-500/10 text-amber-700 dark:text-amber-500'
          : status === 'P'
            ? 'bg-muted text-muted-foreground'
            : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
      )}
    >
      {label}
    </span>
  )
}

function Meta({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className='flex gap-2'>
      <dt>{label}</dt>
      <dd className={cn('text-foreground', mono && 'font-mono')}>{value}</dd>
    </div>
  )
}
